import { createContext, useCallback, useContext, useRef, useState } from "react";
import { StandaloneGuestDetail } from "./StandaloneGuestDetail";

type LauncherContextType = {
  /** Open the guest-detail popup for `guestId`. No-op for empty ids. */
  open: (guestId: string) => void;
};

const GuestDetailLauncherContext = createContext<LauncherContextType | null>(
  null,
);

// Static literals (Tailwind JIT only emits classes it can see in source).
// Depth beyond the ladder reuses the top value — the portals append to
// document.body in mount order, so equal z still stacks newest-on-top.
const Z_LADDER = ["z-[90]", "z-[92]", "z-[94]", "z-[96]", "z-[98]"];

type StackEntry = { key: number; guestId: string };

/**
 * Mounts a STACK of {@link StandaloneGuestDetail} popups and exposes
 * {@link useGuestDetailLauncher} so any descendant can open one for a guest id
 * without drilling `eventId` or owning popup state — including descendants
 * inside an already-open popup (the popup subtree renders under this
 * provider, so a name click there pushes a second layer on top; closing it
 * returns to the previous popup). Place once per page tree (seating client,
 * slotting client, event layout shell). When `eventId` is absent (e.g.
 * template seating) the launcher is inert so callers stay crash-free.
 */
export function GuestDetailLauncherProvider({
  eventId,
  children,
}: {
  eventId?: string;
  children: React.ReactNode;
}) {
  const [stack, setStack] = useState<StackEntry[]>([]);
  const nextKeyRef = useRef(0);

  const open = useCallback(
    (id: string) => {
      if (!eventId || !id) return;
      setStack((prev) =>
        // Re-opening the guest that is already on top would just paint an
        // identical layer over itself — ignore it. A→B→A is still allowed.
        prev.length > 0 && prev[prev.length - 1].guestId === id
          ? prev
          : [...prev, { key: nextKeyRef.current++, guestId: id }],
      );
    },
    [eventId],
  );

  const close = useCallback((key: number) => {
    setStack((prev) => prev.filter((entry) => entry.key !== key));
  }, []);

  return (
    <GuestDetailLauncherContext.Provider value={{ open }}>
      {children}
      {eventId &&
        stack.map((entry, i) => (
          <StandaloneGuestDetail
            key={entry.key}
            eventId={eventId}
            guestId={entry.guestId}
            zClass={Z_LADDER[Math.min(i, Z_LADDER.length - 1)]}
            onClose={() => close(entry.key)}
          />
        ))}
    </GuestDetailLauncherContext.Provider>
  );
}

/**
 * Returns `{ open(guestId) }`. Tolerant when no provider is mounted — `open`
 * is a no-op — so name spots can call it unconditionally.
 */
export function useGuestDetailLauncher(): LauncherContextType {
  const ctx = useContext(GuestDetailLauncherContext);
  return ctx ?? { open: () => {} };
}
